import React, { useEffect, useRef, useState } from 'react';
import { Controls } from '../shared/controls/Controls.jsx';
import { Code } from '../shared/code/Code.jsx';
import { Dropdown } from '../shared/controls/Dropdown.jsx';
import { PillArrow } from './Logo.jsx';
import { DesignPill } from '../shared/design.jsx';
import { Segments, useActiveRect } from './glide.jsx';

const VIEWPORTS = [
  { id: 'fit', label: 'Fit', width: null },
  { id: 'mobile', label: '390', width: 390 },
  { id: 'tablet', label: '768', width: 768 },
  { id: 'desktop', label: '1280', width: 1280 },
];
const MIN_WIDTH = 240;

/**
 * The workspace: one preview iframe that never unmounts (switching between a
 * docs page and a canvas does not reload it), with the story chrome, the
 * panel and the overview laid around it by mode.
 */
export function StoryView({ bridge, entry, mode, state, config, onArgs, onResetArgs, onNavigate, plugins, children }) {
  const frameRef = useRef(null);
  const [viewport, setViewport] = useState(() => readLocal('edl-docs:viewport') ?? 'fit');
  const [tab, setTab] = useState(() => readLocal('edl-docs:tab') ?? 'controls');
  const [source, setSource] = useState(null);
  const [drawer, setDrawer] = useState(null); // plugin panel id open over a docs page
  const [resizing, setResizing] = useState(false);
  const [customWidth, setCustomWidth] = useState(() => Number(readLocal('edl-docs:viewport-width')) || null);
  const [dragging, setDragging] = useState(false);
  const [inspect, setInspect] = useState(false);
  const [paintedId, setPaintedId] = useState(null);
  const frameBoxRef = useRef(null);
  const tabsRef = useRef(null);
  const size = useFrameSize(frameBoxRef, resizing || dragging);

  useEffect(() => {
    bridge.attach(frameRef.current);
    return () => bridge.detach(frameRef.current);
  }, []);

  useEffect(
    () =>
      bridge.onMessage((msg) => {
        if ((msg.type === 'rendered' || msg.type === 'surface') && msg.surface) frameBoxRef.current?.style.setProperty('--ws-surface', `rgb(${msg.surface.join(' ')})`);
        if (msg.type === 'rendered' && 'snippet' in msg) setSource(msg.snippet ?? null);
        if (msg.type === 'rendered' || msg.type === 'error') setPaintedId(msg.id);
        else if (msg.type === 'inspect') setInspect(Boolean(msg.on));
      }),
    [],
  );
  useEffect(() => setSource(null), [state.id]);
  useEffect(() => setResizing(false), [state.id]);
  useEffect(() => writeLocal('edl-docs:viewport', viewport), [viewport]);
  useEffect(() => writeLocal('edl-docs:tab', tab), [tab]);
  useEffect(() => writeLocal('edl-docs:viewport-width', customWidth ?? ''), [customWidth]);

  // Inspect lives in the iframe; the shell owns the switch and re-sends it
  // when the iframe reloads. Only a story canvas can be inspected.
  // Between a story and a docs page the frame changes shape a moment before
  // the preview has drawn the new page, and the old one (often a white story
  // canvas) would flash across it. Hide the iframe until the preview reports
  // a page of the new kind.
  const paintedKind = paintedId ? state.index.entries[paintedId]?.type : null;
  const switching = Boolean(paintedKind) && (mode === 'story' || mode === 'docs') && paintedKind !== mode;
  const inspecting = inspect && mode === 'story';
  useEffect(() => {
    bridge.send({ type: 'inspect', on: inspecting });
    return bridge.onMessage((msg) => msg.type === 'ready' && bridge.send({ type: 'inspect', on: inspecting }));
  }, [inspecting]);
  // The rulers stay mounted while their gutter closes, so they can leave.
  const [rulers, setRulers] = useState(false);
  // Leaving the story (for a docs page, say) drops them at once: their exit
  // only plays when Inspect is switched off over a story.
  useEffect(() => {
    if (inspecting) setRulers(true);
    else if (mode !== 'story') setRulers(false);
  }, [inspecting, mode]);

  // I toggles inspect; [ and ] step through stories.
  const stepRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName ?? '') || el?.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'i') setInspect((v) => !v);
      else if (e.key === 'Escape') setInspect(false);
      else if (e.key === '[') stepRef.current?.(-1);
      else if (e.key === ']') stepRef.current?.(1);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const previewSrc = '/preview.html';
  const viewports = customWidth && viewport === 'custom' ? [...VIEWPORTS, { id: 'custom', label: String(customWidth), width: customWidth }] : VIEWPORTS;
  const vp = viewports.find((v) => v.id === viewport) ?? VIEWPORTS[0];
  const story = state.story;
  const segments = entry ? entry.title.split('/').map((s) => s.trim()) : [];
  const component = segments.at(-1);
  const docsEntry = entry ? state.index.entries[`${entry.componentId}--docs`] : null;
  const siblings = entry ? state.index.order.map((id) => state.index.entries[id]).filter((e) => e.componentId === entry.componentId && e.type === 'story') : [];
  const pluginPanels = plugins.panels;
  const tabs = [
    { id: 'controls', title: 'Controls' },
    { id: 'code', title: 'Code' },
    ...pluginPanels.map((p) => ({ id: `plugin:${p.id}`, title: p.title })),
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : 'controls';
  const underline = useActiveRect(tabsRef, '[aria-selected="true"]', [activeTab, tabs.length, mode]);

  // Previous / next story across the whole library, in sidebar order.
  const storyOrder = entry ? state.index.order.filter((id) => state.index.entries[id].type === 'story') : [];
  const at = entry ? storyOrder.indexOf(entry.id) : -1;
  const prev = at > 0 ? state.index.entries[storyOrder[at - 1]] : null;
  const next = at >= 0 && at < storyOrder.length - 1 ? state.index.entries[storyOrder[at + 1]] : null;
  stepRef.current = mode === 'story' ? (dir) => (dir < 0 ? prev : next) && onNavigate((dir < 0 ? prev : next).id) : null;

  // Dragging the frame's edge: the frame stays centred, so it grows by twice
  // the pointer's travel. Double-click the handle to go back to Fit.
  const startDrag = (e) => {
    const frame = frameBoxRef.current;
    const stage = frame?.parentElement;
    if (!frame || !stage) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const side = handle.dataset.side === 'left' ? -1 : 1;
    const startX = e.clientX;
    // Widths are the story's (the iframe's), not the frame's, which adds the
    // rulers' gutter.
    const startW = frameRef.current.offsetWidth;
    const gutter = parseFloat(getComputedStyle(frame).paddingLeft) || 0;
    const cs = getComputedStyle(stage);
    const maxW = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - gutter;
    setDragging(true);
    const move = (ev) => {
      const w = Math.round(Math.min(maxW, Math.max(MIN_WIDTH, startW + side * 2 * (ev.clientX - startX))));
      setCustomWidth(w);
      setViewport('custom');
    };
    const up = () => {
      setDragging(false);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };
  const fit = () => {
    setResizing(viewport !== 'fit');
    setViewport('fit');
  };

  return (
    <div className={`ws ws--${mode}`}>
      {mode === 'story' && (
        <header className="ws-head">
          <div className="ws-head__titles">
            <p className="edl-label ws-head__eyebrow">
              {segments.slice(0, -1).map((s, i) => (
                <span key={i}>
                  {s}
                  <span className="ws-head__sep">/</span>
                </span>
              ))}
              {docsEntry ? (
                <button type="button" className="ws-head__crumb" onClick={() => onNavigate(docsEntry.id)}>
                  {component}
                </button>
              ) : (
                component
              )}
            </p>
            <div className="ws-head__title-row">
              <h1 className="ws-head__title">{entry.name}</h1>
              <DesignPill link={story?.parameters?.design} />
            </div>
          </div>
          <div className="ws-head__meta">
            <StatusLine state={state} story={story} onRemount={() => bridge.send({ type: 'remount' })} />
          </div>
        </header>
      )}

      {mode === 'story' && (
        <div className="ws-toolbar">
          <Segments
            className="ws-toolbar__group ws-viewports"
            itemClassName="ws-tool"
            label="Viewport width"
            value={vp.id}
            options={viewports.map((v) => ({ value: v.id, label: v.label, title: v.id === 'custom' ? 'Custom width (drag the canvas edge)' : undefined }))}
            onChange={(id) => {
              setResizing(id !== viewport);
              setViewport(id);
            }}
          />
          {siblings.length > 1 && (
            <div className="ws-toolbar__group ws-toolbar__stories">
              <Dropdown variant="bare" className="ws-story-select" value={entry.id} options={siblings.map((s) => ({ value: s.id, label: s.name }))} onChange={onNavigate} aria-label="Story" />
            </div>
          )}
          <div className="ws-toolbar__group">
            <button type="button" className={`ws-tool ws-tool--inspect${inspecting ? ' is-active' : ''}`} aria-pressed={inspecting} onClick={() => setInspect((v) => !v)} title="Measure boxes, spacing and type (I)">
              <span className="ws-tool__check" aria-hidden="true" />
              Inspect
            </button>
          </div>
        </div>
      )}

      <div className="ws-stage">
        <div
          ref={frameBoxRef}
          className={`ws-frame${resizing ? ' is-resizing' : ''}${dragging ? ' is-dragging' : ''}${inspecting ? ' is-inspecting' : ''}${mode === 'story' ? ' has-gutter' : ''}${switching ? ' is-switching' : ''}`}
          style={mode === 'story' && vp.width ? { width: `calc(${vp.width}px + var(--edl-ruler))` } : undefined}
          onTransitionEnd={(e) => e.target === e.currentTarget && e.propertyName === 'width' && setResizing(false)}
        >
          {rulers && mode === 'story' && <Rulers bridge={bridge} open={inspecting} onClosed={() => setRulers(false)} />}
          <iframe ref={frameRef} className="ws-iframe" title={entry ? `${entry.title} preview` : 'Preview'} src={previewSrc} />
          {mode === 'story' && state.status === 'loading' && <div className="ws-loading" aria-hidden="true" />}
          {mode === 'story' &&
            ['left', 'right'].map((side) => (
              <span key={side} className={`ws-handle ws-handle--${side}`} data-side={side} onPointerDown={startDrag} onDoubleClick={fit} title="Drag to resize · double-click to fit" aria-hidden="true" />
            ))}
          {mode === 'story' && size && (
            <span className="ws-size" aria-live="off">
              {size.width} × {size.height}
            </span>
          )}
        </div>
        {/* children is an array of conditionals; [false, false] is truthy and
            an empty overlay would sit over the iframe and eat every wheel. */}
        {React.Children.toArray(children).length > 0 && <div className="ws-overlay">{children}</div>}
      </div>

      {mode === 'docs' && pluginPanels.length > 0 && (
        <>
          <div className="ws-docs-actions" hidden={Boolean(drawer)}>
            {pluginPanels.map((p) => (
              <button key={p.id} type="button" className={`edl-pill edl-pill--small${drawer === p.id ? '' : ' edl-pill--ghost'} ws-docs-action`} onClick={() => setDrawer((d) => (d === p.id ? null : p.id))} aria-expanded={drawer === p.id}>
                {p.title}
                <span className="edl-pill__disc">
                  <PillArrow direction={drawer === p.id ? 'right' : 'up-right'} />
                </span>
              </button>
            ))}
          </div>
          {drawer && (
            <aside className="ws-drawer" aria-label={pluginPanels.find((p) => p.id === drawer)?.title}>
              <div className="ws-drawer__head">
                <p className="edl-label">{pluginPanels.find((p) => p.id === drawer)?.title}</p>
                <button type="button" className="edl-link-button" onClick={() => setDrawer(null)}>
                  Close
                </button>
              </div>
              <div className="ws-drawer__body">
                <PluginPanel key={`${drawer}:${state.id}`} panel={pluginPanels.find((p) => p.id === drawer)} plugins={plugins} state={state} entry={entry} />
              </div>
            </aside>
          )}
        </>
      )}

      {mode === 'story' && (
        <aside className="ws-panel" aria-label="Story panel">
          <div className={`ws-tabs${underline.ready ? ' is-ready' : ''}`} role="tablist" ref={tabsRef} style={underline.rect ? { '--tab-x': `${underline.rect.left}px`, '--tab-w': `${underline.rect.width}px` } : undefined}>
            {tabs.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={activeTab === t.id} className={`ws-tab${activeTab === t.id ? ' is-active' : ''}`} onClick={() => setTab(t.id)}>
                {t.title}
              </button>
            ))}
          </div>
          <div className="ws-panel__body">
            {activeTab === 'controls' &&
              (story ? (
                <Controls argTypes={story.argTypes} args={story.args} initialArgs={story.initialArgs} onChange={onArgs} onReset={onResetArgs} />
              ) : (
                <p className="edl-controls__empty">Loading…</p>
              ))}
            {activeTab === 'code' &&
              (source ? (
                <Code code={source.code} language={source.language} />
              ) : (
                <p className="edl-controls__empty">{state.status === 'rendered' ? 'No source for this story.' : 'Rendering…'}</p>
              ))}
            {activeTab.startsWith('plugin:') && <PluginPanel key={`${activeTab}:${state.id}`} panel={pluginPanels.find((p) => `plugin:${p.id}` === activeTab)} plugins={plugins} state={state} entry={entry} />}
          </div>
          {docsEntry && (
            <div className="ws-panel__foot">
              <button type="button" className="edl-pill edl-pill--small edl-pill--ghost" onClick={() => onNavigate(docsEntry.id)}>
                {component} docs
                <span className="edl-pill__disc">
                  <PillArrow direction="right" />
                </span>
              </button>
            </div>
          )}
        </aside>
      )}
    </div>
  );
}

/**
 * The frame's size while it is changing (a viewport switch or a drag), and
 * for a moment after, so the number can be read once it settles.
 */
/**
 * Inspect's rulers, drawn in the gutter the frame grows while inspecting so
 * they never cover the story. The preview measures and sends `rulers` each
 * frame: the eased box (in the iframe's coordinates) the rulers zero on and
 * its opacity. The ticks take the ink that contrasts with the gutter's colour
 * (--ws-surface, the page's own), black on light and white on dark.
 *
 * The gutter is always there; opening, the ticks grow in from the story's
 * edge outwards, and closing plays it backwards, then calls onClosed.
 */
const RULER_MOTION = 420;
function Rulers({ bridge, open, onClosed }) {
  const ref = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;
  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;
  const wake = useRef(null);
  useEffect(() => wake.current?.(), [open]);
  useEffect(() => {
    const canvas = ref.current;
    const frame = canvas.parentElement;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let last = { rect: null, alpha: 0 };
    let p = 0;
    let raf = 0;
    let prev = 0;
    const easeOut = (t) => 1 - (1 - t) ** 3;
    const draw = () => {
      const size = parseFloat(getComputedStyle(frame).paddingLeft) || 0;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dpr = window.devicePixelRatio || 1;
      // Reallocating the bitmap every frame of the entrance is wasted work.
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      const c = canvas.getContext('2d');
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.globalAlpha = 1;
      c.clearRect(0, 0, w, h);
      const { rect: r, alpha } = last;
      // The gutter's colour, which StoryView keeps from the preview's reports.
      const [sr, sg, sb] = (getComputedStyle(frame).getPropertyValue('--ws-surface').match(/[\d.]+/g) ?? [255, 255, 255]).map(Number);
      const ink = 0.2126 * sr + 0.7152 * sg + 0.0722 * sb > 140 ? '0 0 0' : '255 255 255';
      // From here on, x and y are the iframe's own coordinates.
      c.translate(size, size);
      const e = easeOut(p);
      if (r && size > 1) {
        c.fillStyle = `rgb(${ink} / ${0.1 * alpha * e})`;
        c.fillRect(Math.max(r.left, 0), -size, r.right - Math.max(r.left, 0), size);
        c.fillRect(-size, Math.max(r.top, 0), size, r.bottom - Math.max(r.top, 0));
      }
      const ox = r ? r.left : 0;
      const oy = r ? r.top : 0;
      c.fillStyle = `rgb(${ink})`;
      c.font = '9px ui-monospace, Menlo, monospace';
      c.textBaseline = 'top';
      const len = (i) => (i % 8 === 0 ? 8 : i % 4 === 0 ? 5 : 3);
      // How far a tick has grown: the wave runs from the story's edge out.
      const grown = (at, span) => Math.min(1, Math.max(0, (e - (at / span) * 0.4) / 0.6));
      for (let i = -Math.floor(ox / 8); ox + i * 8 <= w - size; i++) {
        const x = Math.round(ox + i * 8);
        const g = grown(x, w);
        if (!g) continue;
        c.globalAlpha = (i % 8 === 0 ? 1 : 0.45) * g;
        c.fillRect(x, -len(i) * g, 1, len(i) * g);
        if (i % 8 === 0) c.fillText(String(i * 8), x + 3, 2 - size);
      }
      for (let i = -Math.floor(oy / 8); oy + i * 8 <= h - size; i++) {
        const y = Math.round(oy + i * 8);
        const g = grown(y, h);
        if (!g) continue;
        c.globalAlpha = (i % 8 === 0 ? 1 : 0.45) * g;
        c.fillRect(-len(i) * g, y, len(i) * g, 1);
        if (i % 8 === 0) {
          c.save();
          c.translate(2 - size, y + 3);
          c.rotate(Math.PI / 2);
          c.fillText(String(i * 8), 0, -9);
          c.restore();
        }
      }
    };
    const tick = (now) => {
      raf = 0;
      const goal = openRef.current ? 1 : 0;
      const step = reduce ? 1 : (now - prev) / RULER_MOTION;
      prev = now;
      p = goal ? Math.min(1, p + step) : Math.max(0, p - step);
      draw();
      if (p !== goal) raf = requestAnimationFrame(tick);
      else if (!goal) closedRef.current?.();
    };
    wake.current = () => {
      if (raf) return;
      prev = performance.now();
      raf = requestAnimationFrame(tick);
    };
    wake.current();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    const off = bridge.onMessage((msg) => {
      if (msg.type === 'rulers') last = msg;
      else if (msg.type !== 'surface' && msg.type !== 'rendered') return;
      if (!raf) draw();
    });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      off();
      wake.current = null;
    };
  }, []);
  return <canvas ref={ref} className="ws-rulers" aria-hidden="true" />;
}

function useFrameSize(ref, active) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let timer;
    let armed = active;
    const ro = new ResizeObserver(([e]) => {
      if (!armed) return;
      setSize({ width: Math.round(e.contentRect.width), height: Math.round(e.contentRect.height) });
    });
    ro.observe(el);
    if (!active) timer = setTimeout(() => setSize(null), 900);
    return () => {
      armed = false;
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [active]);
  return size;
}

function StatusLine({ state, story, onRemount }) {
  const remount = (
    <button type="button" className="ws-status__remount" onClick={onRemount} aria-label="Remount" title="Remount">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
      </svg>
    </button>
  );
  if (state.status === 'error' && state.error?.notice) return <p className="ws-status">Not pre-rendered</p>;
  if (state.status === 'error') {
    return (
      <p className="ws-status ws-status--error">
        <span className="ws-status__dot" />
        Render failed
        {remount}
      </p>
    );
  }
  if (state.status === 'loading') return <p className="ws-status">Rendering…</p>;
  if (state.status !== 'rendered') return null;
  return (
    <p className="ws-status">
      <span className="ws-status__dot" />
      {story?.server ? 'Server-rendered' : 'Rendered'}
      {remount}
    </p>
  );
}

function PluginPanel({ panel, plugins, state, entry }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!panel || !ref.current) return undefined;
    let cleanup;
    try {
      cleanup = panel.mount(ref.current, { ...plugins.api.getState(), entry });
    } catch (error) {
      console.error(`edl-docs: panel ${panel.id} failed`, error);
      ref.current.textContent = `This panel failed to load: ${error.message}`;
    }
    return () => {
      if (typeof cleanup === 'function') cleanup();
      if (ref.current) ref.current.replaceChildren();
    };
  }, [panel, state.id]);
  return <div ref={ref} className="ws-plugin" />;
}

function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}
